import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faExclamationCircle, 
  faTimes, 
  faArrowRight,
  faSync,
  faBrain,
  faChartLine,
  faUsers,
  faComments,
  faLightbulb,
  faRocket
} from '@fortawesome/free-solid-svg-icons';
import { 
  faInstagram, 
  faFacebook, 
  faTwitter, 
  faLinkedin, 
  faTiktok 
} from '@fortawesome/free-brands-svg-icons';
import { useSocket } from '../../context/SocketContext';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { SocketEventData } from '../../types/socket';

const SocketNotificationModal: React.FC = () => {
  const { socketEvent, clearSocketEvent } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'tr';

  if (!socketEvent) {
    return null;
  }

  const getDataTypeIcon = (dataType: SocketEventData['dataType']) => {
    switch (dataType) {
      case 'instagram':
        return faInstagram;
      case 'facebook':
        return faFacebook;
      case 'twitter':
        return faTwitter;
      case 'linkedin':
        return faLinkedin;
      case 'tiktok':
        return faTiktok;
      case 'aipredictions':
        return faBrain;
      case 'airecommendations':
        return faLightbulb;
      case 'socialmediaaudience':
        return faUsers;
      case 'socialmediaengagement':
        return faComments;
      case 'socialmediaanalytics':
        return faChartLine;
      default:
        return faSync;
    }
  };

  const getDataTypeLabel = (dataType: SocketEventData['dataType']) => {
    switch (dataType) {
      case 'instagram':
        return 'Instagram';
      case 'facebook':
        return 'Facebook';
      case 'twitter':
        return 'Twitter';
      case 'linkedin':
        return 'LinkedIn';
      case 'tiktok':
        return 'TikTok';
      case 'aipredictions':
        return 'AI Tahminleri';
      case 'airecommendations':
        return 'AI Önerileri';
      case 'socialmediaaudience':
        return 'Kitle Analizi';
      case 'socialmediaengagement':
        return 'Etkileşim Analizi';
      case 'socialmediaanalytics':
        return 'Analitik';
      default:
        return 'Genel';
    }
  };

  const getTargetPath = (dataType: SocketEventData['dataType'], operationType: SocketEventData['operationType']) => {
    const resolveSection = (pathname: string) => {
      const pathWithoutLang = pathname.replace(/^\/(tr|en)/, '');
      if (pathWithoutLang.startsWith('/web-site')) return 'web-site';
      if (pathWithoutLang.startsWith('/ads')) return 'ads';
      return 'social-media';
    };
    const section = resolveSection(location.pathname);

    switch (dataType) {
      case 'instagram':
        return `/${currentLang}/${section}/instagram`;
      case 'facebook':
        return `/${currentLang}/${section}/facebook`;
      case 'twitter':
        return `/${currentLang}/${section}/twitter`;
      case 'linkedin':
        return `/${currentLang}/${section}/linkedin`;
      case 'tiktok':
        return `/${currentLang}/${section}/tiktok`;
      case 'aipredictions':
        return `/${currentLang}/${section}/ai-predictions`;
      case 'airecommendations':
        return `/${currentLang}/${section}/ai-recommendations`;
      case 'socialmediaaudience':
        return `/${currentLang}/${section}/audience`;
      case 'socialmediaengagement':
        return `/${currentLang}/${section}/engagement`;
      case 'socialmediaanalytics':
        return `/${currentLang}/${section}/analytics`;
      default:
        return `/${currentLang}/${section}`;
    }
  };

  const isSuccess = socketEvent.action === 'completed';
  const isError = socketEvent.action === 'failed';
  const icon = getDataTypeIcon(socketEvent.dataType);
  const label = getDataTypeLabel(socketEvent.dataType);
  const targetPath = getTargetPath(socketEvent.dataType, socketEvent.operationType);

  const handleGoToPage = () => {
    clearSocketEvent();
    // Sayfayı yenileyerek yönlendir
    window.location.href = targetPath;
  };

  const handleClose = () => {
    clearSocketEvent();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-10"
        >
          <FontAwesomeIcon icon={faTimes} className="text-xl" />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Icon and Status */}
          <div className="flex items-center justify-center mb-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              isSuccess 
                ? 'bg-green-500/20 border-2 border-green-500' 
                : isError 
                ? 'bg-red-500/20 border-2 border-red-500'
                : 'bg-blue-500/20 border-2 border-blue-500'
            }`}>
              <FontAwesomeIcon 
                icon={isSuccess ? faCheckCircle : isError ? faExclamationCircle : icon} 
                className={`text-2xl ${
                  isSuccess 
                    ? 'text-green-400' 
                    : isError 
                    ? 'text-red-400'
                    : 'text-blue-400'
                }`} 
              />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-white text-center mb-2">
            {isSuccess 
              ? `${label} ${socketEvent.operationType === 'sync' ? 'Senkronizasyonu' : 'Analizi'} Tamamlandı`
              : isError
              ? `${label} ${socketEvent.operationType === 'sync' ? 'Senkronizasyonu' : 'Analizi'} Başarısız`
              : `${label} İşlemi`
            }
          </h3>

          {/* Message */}
          {socketEvent.message && (
            <p className="text-white/70 text-center mb-4">
              {socketEvent.message}
            </p>
          )}

          {/* Error message */}
          {socketEvent.error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
              <p className="text-red-400 text-sm text-center">
                {socketEvent.error}
              </p>
            </div>
          )}

          {/* Action Button */}
          {isSuccess && (
            <button
              onClick={handleGoToPage}
              className="w-full bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <span>{label} Sayfasına Git</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          )}

          {/* Close button for error */}
          {isError && (
            <button
              onClick={handleClose}
              className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300"
            >
              Kapat
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocketNotificationModal;

